/*
 * <horizontal-ticker data-speed="1-5">
 * Infinite marquee. The track holds two identical groups and the CSS animation moves it by exactly
 * one group, so the loop is seamless. This element only refines things the CSS cannot know:
 * - keeps the scroll speed constant in px/s whatever the content length or screen size,
 * - adds copies when one group is narrower than the viewport (very short content, very wide screens),
 * - pauses the animation while the ticker is off screen.
 * Reduced motion is handled in CSS (the animation is removed and the row becomes scrollable).
 */
if (!customElements.get('horizontal-ticker')) {
  customElements.define(
    'horizontal-ticker',
    class HorizontalTicker extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.hticker__track');
        this.groups = this.track ? [...this.track.querySelectorAll(':scope > .hticker__group')] : [];
        if (this.groups.length < 2) return;
        this.template = [...this.groups[0].children].filter((el) => !el.hasAttribute('data-added'));
        this.lastWidth = 0;
        this.measure = this.measure.bind(this);
        this.resizeObserver = new ResizeObserver(() => {
          if (Math.abs(this.offsetWidth - this.lastWidth) > 1) this.measure();
        });
        this.resizeObserver.observe(this);
        this.visibility = new IntersectionObserver(([entry]) => {
          this.toggleAttribute('data-offscreen', !entry.isIntersecting);
        });
        this.visibility.observe(this);
        // Images change the group width once they load.
        this.querySelectorAll('img').forEach((img) => {
          if (!img.complete) img.addEventListener('load', this.measure, { once: true });
        });
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.measure);
        this.measure();
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        this.visibility?.disconnect();
      }

      measure() {
        this.lastWidth = this.offsetWidth;
        let groupWidth = this.groups[0].scrollWidth;
        let guard = 0;
        while (groupWidth > 0 && groupWidth < this.lastWidth && guard < 8) {
          this.groups.forEach((group) => {
            this.template.forEach((item) => {
              const copy = item.cloneNode(true);
              copy.setAttribute('data-added', '');
              copy.setAttribute('aria-hidden', 'true');
              copy.removeAttribute('data-shopify-editor-block');
              copy.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));
              copy.querySelectorAll('img').forEach((img) => img.setAttribute('alt', ''));
              group.append(copy);
            });
          });
          groupWidth = this.groups[0].scrollWidth;
          guard++;
        }
        if (!groupWidth) return;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const distance = groupWidth + gap;
        const pxPerSecond = 30 * (parseFloat(this.dataset.speed) || 3);
        this.style.setProperty('--hticker-distance', `${-distance}px`);
        this.style.setProperty('--hticker-duration', `${(distance / pxPerSecond).toFixed(2)}s`);
      }
    }
  );
}
